import { z } from 'zod'

import { historyCountSchema } from '../shared/conversation-attention'

// Needs attention is outstanding work, by conversation; Unread is the
// appointment updates this staff member has not read.
export const notificationFilterSchema = z.enum([
  'ALL',
  'NEEDS_ATTENTION',
  'UNREAD',
])
export type NotificationFilter = z.infer<typeof notificationFilterSchema>

const appointmentRouteTargetSchema = z
  .object({
    type: z.literal('APPOINTMENTS'),
  })
  .strict()

const notificationBase = {
  schemaVersion: z.literal(1),
  notificationId: z.string().regex(/^[0-9a-f]{64}$/),
  severity: z.literal('INFO'),
  title: z.string().min(1).max(80),
  description: z.string().min(1).max(240),
  occurredAtMs: z.number().int().positive(),
  remainsVisibleAfterRead: z.literal(true),
  readAtMs: z.number().int().positive().nullable(),
}

const informationalNotificationSchema = z
  .object({
    ...notificationBase,
    type: z.enum([
      'APPOINTMENT_BOOKED',
      'APPOINTMENT_CONFIRMED',
      'APPOINTMENT_CANCELLED',
    ]),
    requiresAction: z.literal(false),
    dismissible: z.literal(true),
    routeTarget: appointmentRouteTargetSchema,
  })
  .strict()

// One notification per conversation, carrying its Conversations status: it
// requires action until staff complete or review it, then stays as read
// history. It has no per-user read state and cannot be dismissed.
const conversationNotificationSchema = z
  .object({
    ...notificationBase,
    type: z.enum([
      'CONVERSATION_NEEDS_ACTION',
      'CONVERSATION_NEEDS_REVIEW',
      'CONVERSATION_ACTION_COMPLETED',
      'CONVERSATION_REVIEWED',
    ]),
    requiresAction: z.boolean(),
    dismissible: z.literal(false),
    routeTarget: z
      .object({
        type: z.literal('CONVERSATIONS'),
        interactionAggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
      })
      .strict(),
  })
  .strict()
  .refine(
    (value) =>
      value.requiresAction ===
        (value.type === 'CONVERSATION_NEEDS_ACTION' ||
          value.type === 'CONVERSATION_NEEDS_REVIEW') &&
      value.requiresAction === (value.readAtMs === null),
    'Outstanding work is unread; settled work is read history'
  )

export const dashboardNotificationSchema = z.union([
  informationalNotificationSchema,
  conversationNotificationSchema,
])

export type DashboardNotification = z.infer<typeof dashboardNotificationSchema>

export const notificationFeedSchema = z
  .object({
    schemaVersion: z.literal(2),
    notifications: z.array(dashboardNotificationSchema).max(50),
    outstandingCount: historyCountSchema,
    needsActionCount: historyCountSchema,
    needsReviewCount: historyCountSchema,
    // Unread appointment updates, which Mark all read clears.
    unreadCount: z.number().int().nonnegative().max(2000),
    nextCursor: z.string().min(1).max(512).nullable(),
  })
  .strict()

export type NotificationFeed = z.infer<typeof notificationFeedSchema>

export const notificationMutationSchema = z
  .object({
    schemaVersion: z.literal(1),
    notificationId: z.string().regex(/^[0-9a-f]{64}$/),
    readAtMs: z.number().int().positive(),
    dismissedAtMs: z.number().int().positive().nullable(),
  })
  .strict()

export const markAllNotificationsReadSchema = z
  .object({
    schemaVersion: z.literal(1),
    readThroughAtMs: z.number().int().positive(),
  })
  .strict()

export type NotificationMutation = z.infer<typeof notificationMutationSchema>
export type MarkAllNotificationsRead = z.infer<
  typeof markAllNotificationsReadSchema
>
